import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Loader2, Lock, Sparkles, CheckCircle2, ListChecks, History, Info, Wrench, Lightbulb, TrendingUp, TrendingDown, Minus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";

export function ResumeReview({ isPro, hasContent, onView }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [fb, setFb] = useState(null);
  const [bank, setBank] = useState([]);
  const [quota, setQuota] = useState(null); // server-side allowance from /api/usage → features.resume_review {used, limit, remaining}
  const loadQuota = () => api.get("/usage").then(({ data }) => setQuota(data?.features?.resume_review || null)).catch(() => {});
  const fileRef = useRef(null);
  const feedbackRef = useRef(null);
  const loadBank = () => api.get("/resume/reviews").then(({ data }) => setBank(data.items)).catch(() => {});
  useEffect(() => { if (isPro) { loadBank(); loadQuota(); } }, [isPro]);
  useEffect(() => { onView?.(fb); }, [fb, onView]);
  const openReview = (r) => {
    setFb(r);
    setTimeout(() => feedbackRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const run = async (file) => {
    setBusy(true);
    try {
      const form = new FormData();
      if (file) form.append("file", file);
      const { data } = await api.post("/resume/review", form, { headers: { "Content-Type": "multipart/form-data" } });
      setFb(data); loadBank();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); loadQuota(); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!isPro) return (
    <div className="bg-slate-950 text-white rounded-2xl p-8 text-center" data-testid="resume-review-locked">
      <Lock className="w-8 h-8 mx-auto text-amber-400" />
      <h2 className="font-heading text-2xl mt-3">AI Resume Review is a Premium feature</h2>
      <p className="text-slate-300 text-sm mt-2 max-w-md mx-auto">Upload your existing resume — or review the one you just built — and a professional resume-editor AI scores how it reads: formatting, writing, organization and clarity, with fire-service tips on the side.</p>
      <Button onClick={() => navigate("/pricing")} className="mt-5 bg-amber-400 text-slate-950 hover:bg-amber-300" data-testid="resume-review-upgrade">See Premium plans</Button>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="resume-review">
      <div>
        <h2 className="font-heading text-2xl text-slate-900">Get honest feedback.</h2>
        <p className="text-sm text-slate-500 mt-1">Optional — your resume is complete without it. A professional resume-editor AI scores the document itself — formatting, writing, organization and readability — then points out what to change. Optional fire-service additions are listed separately and never lower your score. Review the resume you built here, or upload one (PDF, DOCX or TXT).</p>
        <p className="text-[11px] text-slate-400 mt-1" data-testid="resume-review-disclaimer">AI-generated suggestions that may miss things — verify against each department's posting and review the final resume yourself. No guarantee of interviews or hiring.</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => e.target.files?.[0] && run(e.target.files[0])} data-testid="resume-review-file" />
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="h-11" data-testid="resume-review-upload-button"><Upload className="w-4 h-4 mr-2" /> Upload a resume to review</Button>
        <Button onClick={() => run(null)} disabled={busy || !hasContent} className="bg-slate-900 hover:bg-slate-800 h-11" data-testid="resume-review-current-button">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Review the resume I built</Button>
      </div>
      {quota && (
        <p className={`text-xs ${quota.remaining > 0 ? "text-slate-500" : "text-amber-700"}`} data-testid="resume-review-quota">
          <span className="font-semibold" data-testid="resume-review-quota-remaining">{quota.remaining}</span> of {quota.limit} AI reviews remaining this month · resets on the 1st
        </p>
      )}
      {busy && <p className="text-sm text-slate-500" data-testid="resume-review-busy">Reading it the way a resume editor would…</p>}
      {fb && <div ref={feedbackRef} className="scroll-mt-24"><Feedback fb={fb} /></div>}
      {bank.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="resume-review-bank">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Saved reviews</p>
          <p className="text-xs text-slate-500 mt-1">Every AI review you've run, newest first — click one to open it above and see how your resume has changed.</p>
          <ScoreTrend bank={bank} />
          <ul className="mt-3 divide-y divide-slate-100">
            {bank.map((r, i) => {
              const open = fb && (fb.review_id ? fb.review_id === r.review_id : fb.reviewed_at === r.reviewed_at);
              const prev = bank.slice(i + 1).find((p) => p.score != null);
              const delta = r.score != null && prev ? r.score - prev.score : null;
              return (
                <li key={r.review_id || r.reviewed_at} role="button" tabIndex={0} onClick={() => openReview(r)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openReview(r); } }}
                    className={`py-2 px-2 -mx-2 rounded-lg flex items-center justify-between gap-3 cursor-pointer transition-colors ${open ? "bg-slate-100" : "hover:bg-slate-50"}`} data-testid={`resume-review-item-${r.review_id}`}>
                  <span className="text-sm text-slate-800"><b>{r.score != null ? `${r.score}/100` : "Review"}</b>{delta != null && <Delta value={delta} testId={`resume-review-delta-${r.review_id}`} />} <span className="text-xs text-slate-500">· {new Date(r.reviewed_at).toLocaleDateString()} · {r.source === "upload" ? "uploaded file" : "built resume"}</span>{r.headline && <span className="block text-xs text-slate-500 truncate max-w-md">{r.headline}</span>}</span>
                  <Button size="sm" variant={open ? "default" : "outline"} onClick={(e) => { e.stopPropagation(); openReview(r); }} data-testid={`resume-review-open-${r.review_id}`}>{open ? "Viewing" : "Open"}</Button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function WhatToChange({ items }) {
  const [openIdx, setOpenIdx] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? items : items.slice(0, 4);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="resume-feedback-improvements">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <p className="font-heading text-lg text-slate-900 flex items-center gap-2"><Wrench className="w-4 h-4 text-primary" /> What to change</p>
        <span className="text-xs text-slate-400">{items.length} item{items.length === 1 ? "" : "s"} · tap one to see the fix</span>
      </div>
      {items.length === 0 && <p className="text-sm text-slate-500">Nothing to fix — this one reads clean.</p>}
      <ol className="divide-y divide-slate-100">
        {visible.map((it, i) => {
          const open = openIdx === i;
          return (
            <li key={i} data-testid={`resume-fix-${i}`}>
              <button type="button" onClick={() => setOpenIdx(open ? -1 : i)} aria-expanded={open} className="w-full text-left py-2.5 flex items-start gap-3 group" data-testid={`resume-fix-toggle-${i}`}>
                <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="flex-1 min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-2">{it.area}</span>
                  <span className="text-sm text-slate-800">{it.issue}</span>
                </span>
                <ChevronDown className={`w-4 h-4 shrink-0 mt-1 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="ml-9 mb-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-800" data-testid={`resume-fix-detail-${i}`}>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-primary mr-2">Fix</span>{it.fix}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {items.length > 4 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-2 text-sm font-semibold text-primary hover:underline" data-testid="resume-fix-show-all">
          {showAll ? "Show fewer" : `Show ${items.length - 4} more`}
        </button>
      )}
    </div>
  );
}

function Delta({ value, testId }) {
  const cls = value > 0 ? "text-green-600 bg-green-50" : value < 0 ? "text-red-600 bg-red-50" : "text-slate-500 bg-slate-100";
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  return <span className={`ml-1.5 inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${cls}`} data-testid={testId}><Icon className="w-3 h-3" />{value > 0 ? `+${value}` : value === 0 ? "same" : value}</span>;
}

function ScoreTrend({ bank }) {
  const scored = bank.filter((r) => r.score != null);
  if (scored.length < 2) return null;
  const latest = scored[0], first = scored[scored.length - 1];
  const delta = latest.score - first.score;
  const path = [...scored].reverse();
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid="resume-review-trend">
      <span className="text-slate-600">First review <b className="text-slate-900">{first.score}</b> → latest <b className="text-slate-900">{latest.score}</b></span>
      <Delta value={delta} testId="resume-review-trend-delta" />
      <span className="text-xs text-slate-400 font-mono" data-testid="resume-review-trend-path">{path.map((r) => r.score).join(" · ")}</span>
    </div>
  );
}

function Feedback({ fb }) {
  const color = fb.score >= 80 ? "text-green-600" : fb.score >= 60 ? "text-amber-600" : "text-red-600";
  const recommended = fb.recommended || fb.missing || [];
  return (
    <div className="space-y-4" data-testid="resume-feedback">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-5">
          <div className={`font-heading text-5xl font-bold ${color}`} data-testid="resume-feedback-score">{fb.score}</div>
          <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Overall resume score · {fb.source === "upload" ? "uploaded file" : "your built resume"}</p><p className="text-slate-800 font-medium">{fb.headline}</p></div>
        </div>
        {fb.score_explanation && (
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="resume-feedback-score-explanation">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Why this score</p>
            <p className="text-sm text-slate-700 mt-1">{fb.score_explanation}</p>
            <p className="text-[11px] text-slate-400 mt-1.5">Scored on formatting, writing, organization, readability and professional effectiveness — not on certifications or experience you don't have yet.</p>
          </div>
        )}
      </div>
      <Card icon={CheckCircle2} tone="text-green-600" title="What's working" items={fb.strengths} testId="resume-feedback-strengths" />
      <WhatToChange items={fb.improvements || []} />
      {recommended.length > 0 && (
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-5" data-testid="resume-feedback-recommended">
          <p className="font-heading text-lg text-slate-900 mb-1 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-sky-600" /> Recommended</p>
          <p className="text-xs text-slate-500 mb-2">Optional additions to consider over time — certifications, training, experience. These don't affect your score.</p>
          <ul className="space-y-1.5 text-sm list-disc pl-5 text-slate-700">{recommended.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="resume-feedback-organization"><p className="font-heading text-lg text-slate-900 mb-1">Organization</p><p className="text-sm text-slate-600">{fb.organization}</p></div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="resume-feedback-presentation"><p className="font-heading text-lg text-slate-900 mb-1">Presentation</p><p className="text-sm text-slate-600">{fb.presentation}</p></div>
      </div>
      {(fb.fire_service_tips || []).length > 0 && <Card icon={ListChecks} tone="text-primary" title="Fire-service tips" items={fb.fire_service_tips} dark testId="resume-feedback-tips" />}
    </div>
  );
}

function Card({ icon: Icon, tone, title, items, dark, testId }) {
  return (
    <div className={`${dark ? "bg-slate-950 text-white" : "bg-white border border-slate-200"} rounded-2xl p-5`} data-testid={testId}>
      <p className={`font-heading text-lg mb-2 flex items-center gap-2 ${dark ? "text-white" : "text-slate-900"}`}><Icon className={`w-4 h-4 ${tone}`} /> {title}</p>
      <ul className={`space-y-1.5 text-sm list-disc pl-5 ${dark ? "text-slate-200" : "text-slate-700"}`}>{(items || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
    </div>
  );
}
